import { Button, Checkbox, FormGroup } from '@zoom/components';
import { FormApi } from 'final-form';
import { equals } from 'ramda';
import React, { useMemo, useState } from 'react';
import { FormProps } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { useTranslation } from 'react-i18next';

import { TimeDurationPickerField } from '../../components/DatePicker/TimeDurationPicker';
import { Legend } from '../../components/Form/Legend';
import { ModalConfirmation } from '../../components/ModalConfirmation';
import { SidebarBody, SidebarHeader, SidebarFormWrapper } from '../../components/layout/Sidebar';
import { AdherenceNotificationType } from '../../graphql/components';
import { convertToHoursMinutes } from '../../utils/dateRange';
import { ONE_HOUR_IN_SECONDS } from '../../utils/time';
import { required } from '../../utils/validations';
import {
  getFieldsInputTag,
  SAVE_NOTIFICATION_SETTINGS_BUTTON_TAG,
  CANCEL_NOTIFICATION_SETTINGS_BUTTON_TAG,
} from './constants';

type AdherenceNotificationFormSidebarProps = {
  onSubmit: FormProps<AdherenceNotificationFormValues>['onSubmit'];
  onCancel: () => void;
  notificationSetup: AdherenceNotificationType | null;
};

export type AdherenceNotificationFormValues = ReturnType<typeof formatValues>;

/**
 * Format `AdherenceNotificationType` to fit `Form` input fields
 */
export const formatValues = (notificationSetup: AdherenceNotificationType | null) => {
  if (!notificationSetup) {
    return {
      pushNotifications: false,
      threshold: 0,
    };
  }

  return {
    pushNotifications: true,
    threshold: convertToHoursMinutes(notificationSetup.threshold), // Due to TimeDurationPickerField to show proper value
  };
};

export const AdherenceNotificationFormSidebar: React.FC<AdherenceNotificationFormSidebarProps> = ({
  onCancel: handleCancel,
  onSubmit: handleSubmit,
  notificationSetup,
}) => {
  const { t } = useTranslation();

  const initialValues = useMemo(() => formatValues(notificationSetup), [notificationSetup]);

  const [showDisableNotificationsModal, setShowDisableNotificationsModal] = useState(false);

  const handleCancelDisableNotificationsModal =
    (form: FormApi<AdherenceNotificationFormValues, Partial<AdherenceNotificationFormValues>>, value: boolean) =>
    () => {
      setShowDisableNotificationsModal(false);
      form.change('pushNotifications', value);
    };

  return (
    <SidebarFormWrapper
      className="adherence-notification-sidebar"
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ form }) => {
        const { pushNotifications } = form.getState().values;
        return (
          <>
            <OnChange name="pushNotifications">
              {(value) => {
                if (initialValues.pushNotifications && !value) {
                  setShowDisableNotificationsModal(true);
                }
              }}
            </OnChange>
            <SidebarHeader onClose={handleCancel}>
              <Button
                data-test-id={SAVE_NOTIFICATION_SETTINGS_BUTTON_TAG}
                disabled={equals(form.getState().values, initialValues)}
                kind="secondary"
                type="submit"
              >
                {t('layout.action.save')}
              </Button>
              <Button
                data-test-id={CANCEL_NOTIFICATION_SETTINGS_BUTTON_TAG}
                className="button-icon-left mr-auto ml-2"
                color="info"
                icon={{ color: 'red', kind: 'cross', size: 10 }}
                onClick={handleCancel}
              >
                {t('layout.action.cancel')}
              </Button>
            </SidebarHeader>
            <SidebarBody>
              <div className="p-4">
                <fieldset>
                  <Legend>{t('scheduleAdherence.realTimeAdherence.rtaNotifications')}</Legend>
                  <FormGroup>
                    <Checkbox
                      data-test-id={getFieldsInputTag('push-notifications-checkbox')}
                      input={{ value: pushNotifications, name: 'pushNotifications' }}
                      onClick={() => form.change('pushNotifications', !pushNotifications)}
                    >
                      <span className="font-weight-bold">{t('notification.pushNotifications')}</span>
                    </Checkbox>
                  </FormGroup>
                  <FormGroup>
                    <TimeDurationPickerField
                      data-test-id={getFieldsInputTag('notifications-threshold')}
                      disabled={!pushNotifications}
                      label={t('scheduleAdherence.realTimeAdherence.notifyThreshold')}
                      max={ONE_HOUR_IN_SECONDS * 11}
                      minSecOption
                      name="threshold"
                      validate={required}
                    />
                  </FormGroup>
                </fieldset>
              </div>
            </SidebarBody>

            {showDisableNotificationsModal && (
              <ModalConfirmation
                bodyText={
                  <>
                    <p>
                      <strong>{t('layout.confirmationRequired')}</strong>
                    </p>
                    <p>{t('scheduleAdherence.realTimeAdherence.pushNotificationsModal.body')}</p>
                    <p>{t('notification.modalBodyDisableSteps')}</p>
                  </>
                }
                closeText={t('layout.action.cancel')}
                headerText={t('layout.warning')}
                onClose={handleCancelDisableNotificationsModal(form, true)}
                onSubmit={handleCancelDisableNotificationsModal(form, false)}
                show={showDisableNotificationsModal}
                submitText={t('layout.action.confirm')}
              />
            )}
          </>
        );
      }}
    </SidebarFormWrapper>
  );
};
